import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { MediaItem } from '@/types/posts';
import { Post, PostMedia } from '@/services/posts';
import { PlatformOptions } from '@/types/scheduled-instances';
import { devLog } from '@/utils/logger';

/**
 * Composer Store
 *
 * Unified Zustand store for post creation/editing with mode awareness.
 * Supports 4 modes:
 * 1. fresh_create - New post, no context
 * 2. calendar_create - New post with date, map to calendar day
 * 3. edit_post - Edit existing post
 * 4. commit_mapped - Commit mapped post from calendar (shooting mode)
 */

/**
 * NOTE: there is intentionally no "editable status" list here. Editing is decided per item by the
 * release rulebook (server: `release-policy.service.ts`; client mirror: `usePostPermissions`;
 * composers: `usePostReleasePermissions`), never by the raw `post.status` cache (which is also the
 * calendar MAPPING status for mapped entries). The store loads every post; locked content is
 * shown read-only, never blank.
 */

/**
 * Calendar mapping data structure for loadFromMapping action
 * Represents a post mapped to a calendar date with optional variation
 */
export interface CalendarMappingData {
  id: number;
  calendarId: number;
  plannedDate: string;
  status: string;
  post?: {
    id: number;
    caption: string;
    status: string;
    contentStatus?: string;
    labels?: string[];
    postMedia?: Array<{
      id: number;
      originalUrl: string;
      thumbnailUrl?: string | null;
      type: string;
      order: number;
      width?: number;
      height?: number;
      size?: number;
    }>;
  };
  variation?: {
    id: number;
    title: string;
    caption?: string;
    labels?: string[];
    postMedia?: Array<{
      id: number;
      originalUrl: string;
      thumbnailUrl?: string | null;
      type: string;
      order: number;
      width?: number;
      height?: number;
      size?: number;
    }>;
  };
}

interface ComposerState {
  // Post content
  caption: string;
  selectedChannels: number[];
  media: MediaItem[];
  scheduledTime: Date | null;
  labels: string[];
  calendarId: number | null;
  calendarNoteId: number | null;
  calendarHolidayId: number | null;
  channelOptions: Record<number, PlatformOptions>;

  // Edit mode state
  editingPostId: number | null;
  originalPost: Post | null;
  existingMediaIds: number[]; // Track IDs of media that came from existing post
  removedMediaIds: number[]; // Track IDs of media to remove on save

  // Media thumbnail changes (deferred save)
  changedThumbnails: Map<string, string>; // mediaId -> thumbnailUrl

  // V2: Mode & Mapping Fields
  mode: 'fresh_create' | 'calendar_create' | 'edit_post' | 'commit_mapped';
  calendarMappingId: number | null; // From mapping prop or save response
  variationId: number | null; // If mapping has a variation
  saveTarget: 'post' | 'variation'; // Where to persist edits
  isReschedule: boolean; // Edit mode flag: clear channels/time
  // Idempotency-Key for the create of a NEW post: generated lazily on the first create-save, kept across
  // retries, cleared when the create is confirmed or the composer is reset (see utils/saveResilience.ts).
  createIntentKey: string | null;
  // ISO timestamp frozen for a shoot_now CREATE, so every retry of the same intent sends the same body (the
  // server hashes it). Lives and dies with createIntentKey.
  createShootAt: string | null;

  // UI state
  isModalOpen: boolean;
  isSchedulerOpen: boolean;
  isSaving: boolean;
  isCarouselMode: boolean;
  /** The user flipped the carousel toggle (the edit-mode auto-derivation does not count). Reset on load. */
  carouselToggledByUser: boolean;
  /**
   * Whose content the form holds, for the autosave: 'new' = a brand-new post (the only owner whose draft may be
   * written under the 'new' key), a post id = loaded for editing, null = mapping / left over (nobody's draft).
   */
  draftOwner: 'new' | number | null;
  /** Brand the 'new' draft belongs to, pinned when the create composer opens. Autosave writes only while it equals the live team. */
  draftTeamId: number | null;

  // Actions
  setCaption: (caption: string) => void;
  addChannel: (channelId: number) => void;
  removeChannel: (channelId: number) => void;
  toggleChannel: (channelId: number) => void;
  setChannels: (channelIds: number[]) => void;
  addMedia: (media: MediaItem) => void;
  removeMedia: (mediaId: string) => void;
  setMedia: (media: MediaItem[]) => void;
  setScheduledTime: (time: Date | null) => void;
  setLabels: (labels: string[]) => void;
  addLabel: (label: string) => void;
  removeLabel: (label: string) => void;
  setCalendarId: (calendarId: number | null) => void;
  setCalendarNoteId: (calendarNoteId: number | null) => void;
  setCalendarHolidayId: (calendarHolidayId: number | null) => void;
  setCalendarEvent: (noteId: number | null, holidayId: number | null) => void;
  setCarouselMode: (isCarousel: boolean) => void;
  /** Carousel toggle clicked by the user: also marks the choice as explicit so a save sends it. */
  setCarouselModeByUser: (isCarousel: boolean) => void;
  reorderMedia: (fromIndex: number, toIndex: number) => void;
  setChangedThumbnail: (mediaId: string, thumbnailUrl: string) => void;
  clearChangedThumbnails: () => void;
  setChannelOptions: (options: Record<number, PlatformOptions>) => void;
  updateChannelOptions: (channelId: number, options: PlatformOptions) => void;
  openModal: () => void;
  closeModal: () => void;
  toggleScheduler: () => void;
  setIsSaving: (isSaving: boolean) => void;
  reset: () => void;

  // Edit mode actions
  loadPost: (post: Post) => void;
  clearEditMode: () => void;
  isEditing: () => boolean;

  // V2: New mode-aware actions
  setMode: (mode: ComposerState['mode']) => void;
  setCalendarMappingId: (id: number | null) => void;
  setVariationId: (id: number | null) => void;
  setSaveTarget: (target: 'post' | 'variation') => void;
  setIsReschedule: (isReschedule: boolean) => void;
  setCreateIntentKey: (key: string | null) => void;
  setCreateShootAt: (iso: string | null) => void;
  loadFromMapping: (mapping: CalendarMappingData) => void;

  // V2: Computed alias for consistency with useShootPost hook
  existingPost: Post | null;
}

const initialState = {
  caption: '',
  selectedChannels: [] as number[],
  media: [] as MediaItem[],
  scheduledTime: null as Date | null,
  labels: [] as string[],
  calendarId: null as number | null,
  calendarNoteId: null as number | null,
  calendarHolidayId: null as number | null,
  channelOptions: {} as Record<number, PlatformOptions>,

  // Edit mode state
  editingPostId: null as number | null,
  originalPost: null as Post | null,
  existingMediaIds: [] as number[],
  removedMediaIds: [] as number[],

  // Media thumbnail changes
  changedThumbnails: new Map<string, string>(),

  // V2: Mode & Mapping Fields
  mode: 'fresh_create' as const,
  calendarMappingId: null as number | null,
  variationId: null as number | null,
  saveTarget: 'post' as const,
  isReschedule: false,
  createIntentKey: null as string | null,
  createShootAt: null as string | null,

  // UI state
  isModalOpen: false,
  isSchedulerOpen: false,
  isSaving: false,
  isCarouselMode: false,
  carouselToggledByUser: false,
  draftOwner: 'new' as 'new' | number | null,
  draftTeamId: null as number | null,
};

/**
 * Convert PostMedia from API to MediaItem for composer
 */
const postMediaToMediaItem = (postMedia: PostMedia): MediaItem => {
  const convertedType = postMedia.type.toLowerCase() as 'image' | 'video' | 'pdf';

  return {
    id: String(postMedia.id),
    url: postMedia.originalUrl ?? '',
    type: convertedType,
    thumbnail: postMedia.thumbnailUrl || undefined,
    width: postMedia.width,
    height: postMedia.height,
    size: postMedia.size,
    sourceType: postMedia.sourceType,
    driveFileId: postMedia.sourceRef?.driveFileId,
  };
};

export const useComposerStore = create<ComposerState>()(
  devtools(
    (set, get) => ({
      ...initialState,

      // existingPost initialized as null (synced with originalPost in loadPost/loadFromMapping)
      existingPost: null as Post | null,

      setCaption: (caption) => set({ caption }),

      addChannel: (channelId) =>
        set((state) => ({
          selectedChannels: [...state.selectedChannels, channelId],
        })),

      removeChannel: (channelId) =>
        set((state) => ({
          selectedChannels: state.selectedChannels.filter((id) => id !== channelId),
        })),

      toggleChannel: (channelId) =>
        set((state) => ({
          selectedChannels: state.selectedChannels.includes(channelId)
            ? state.selectedChannels.filter((id) => id !== channelId)
            : [...state.selectedChannels, channelId],
        })),

      setChannels: (channelIds) => set({ selectedChannels: channelIds }),

      addMedia: (media) =>
        set((state) => ({
          media: state.media.some((m) => m.id === media.id)
            ? state.media  // Skip duplicate
            : [...state.media, media],
        })),

      removeMedia: (mediaId) =>
        set((state) => ({
          media: state.media.filter((m) => m.id !== mediaId),
        })),

      setMedia: (media) => set({ media }),

      setScheduledTime: (time) => set({ scheduledTime: time }),

      setLabels: (labels) => set({ labels }),

      addLabel: (label) =>
        set((state) => ({
          labels: state.labels.includes(label)
            ? state.labels
            : [...state.labels, label],
        })),

      removeLabel: (label) =>
        set((state) => ({
          labels: state.labels.filter((l) => l !== label),
        })),

      setCalendarId: (calendarId) => set({ calendarId }),

      setCalendarNoteId: (calendarNoteId) => set({ calendarNoteId }),

      setCalendarHolidayId: (calendarHolidayId) => set({ calendarHolidayId }),

      setCalendarEvent: (noteId, holidayId) => set({
        calendarNoteId: noteId,
        calendarHolidayId: holidayId
      }),

      setCarouselMode: (isCarousel) => set({ isCarouselMode: isCarousel }),
      setCarouselModeByUser: (isCarousel) => set({ isCarouselMode: isCarousel, carouselToggledByUser: true }),

      reorderMedia: (fromIndex, toIndex) =>
        set((state) => {
          const newMedia = [...state.media];
          const [movedItem] = newMedia.splice(fromIndex, 1);
          newMedia.splice(toIndex, 0, movedItem);
          return { media: newMedia };
        }),

      setChangedThumbnail: (mediaId, thumbnailUrl) =>
        set((state) => {
          const newMap = new Map(state.changedThumbnails);
          newMap.set(mediaId, thumbnailUrl);
          return { changedThumbnails: newMap };
        }),

      clearChangedThumbnails: () =>
        set({ changedThumbnails: new Map() }),

      setChannelOptions: (options) => set({ channelOptions: options }),

      updateChannelOptions: (channelId, options) =>
        set((state) => ({
          channelOptions: { ...state.channelOptions, [channelId]: options },
        })),

      openModal: () => set({ isModalOpen: true }),

      closeModal: () => set({ isModalOpen: false }),

      toggleScheduler: () =>
        set((state) => ({ isSchedulerOpen: !state.isSchedulerOpen })),

      setIsSaving: (isSaving) => set({ isSaving }),

      reset: () => set({
        ...initialState,
        // Ensure V2 fields are also reset
        existingPost: null,
        mode: 'fresh_create' as const,
        calendarMappingId: null,
        variationId: null,
        saveTarget: 'post' as const,
        isReschedule: false,
        createIntentKey: null,
        createShootAt: null,
      }),

      // Edit mode actions
      loadPost: (post: Post) => {
        // DEBUG: Log the incoming post data
        devLog.log('[ComposerStore] loadPost called with:', {
          postId: post.id,
          postType: post.type,
          status: post.status,
          mediaCount: post.postMedia?.length || 0,
          postMediaTypes: post.postMedia?.map(m => ({
            id: m.id,
            type: m.type,
            url: m.originalUrl,
          })) || [],
        });

        // Convert post media to MediaItem format
        const mediaItems = post.postMedia?.map(postMediaToMediaItem) || [];

        // DEBUG: Log converted media items
        devLog.log('[ComposerStore] Converted mediaItems:', mediaItems.map(m => ({
          id: m.id,
          type: m.type,
          url: m.url,
        })));

        // Extract channel IDs from post channels — exclude any that have
        // since been deactivated (e.g. user disconnected the social account).
        // Otherwise the inactive channel sits silently in `selectedChannels`
        // and the next shoot fails with "invalid or inactive".
        const channelIds = post.postChannels
          ?.filter((pc) => pc.channel?.isActive !== false)
          .map((pc) => pc.channelId) || [];

        // Track existing media IDs
        const existingMediaIds = post.postMedia?.map((m) => m.id) || [];

        // Parse scheduled time if exists
        const scheduledTime = post.scheduledAt ? new Date(post.scheduledAt) : null;

        // DEBUG: Log carousel mode determination
        const isCarousel = post.type === 'CAROUSEL';
        devLog.log('[ComposerStore] Setting isCarouselMode:', {
          isCarouselMode: isCarousel,
          postType: post.type,
          mediaCount: mediaItems.length,
        });

        set({
          caption: post.caption,
          selectedChannels: channelIds,
          media: mediaItems,
          scheduledTime,
          labels: post.labels || [],
          channelOptions: {},
          changedThumbnails: new Map(),
          calendarNoteId: null,
          calendarHolidayId: null,
          // Use CalendarMapping (NEW system) - calendarPost is deprecated
          calendarId: post.calendarMappings?.[0]?.calendarId || null,
          editingPostId: post.id,
          originalPost: post,
          existingPost: post, // Synced alias
          existingMediaIds,
          removedMediaIds: [],
          isCarouselMode: isCarousel,
          carouselToggledByUser: false,
          draftOwner: post.id,
          mode: 'edit_post', // V2: Set mode
        });

        devLog.log(`[ComposerStore] Loaded post ${post.id} for editing - isCarouselMode: ${isCarousel}`);
      },

      clearEditMode: () =>
        set({
          editingPostId: null,
          originalPost: null,
          existingMediaIds: [],
          removedMediaIds: [],
        }),

      isEditing: (): boolean => {
        return get().editingPostId !== null;
      },

      // V2: New mode-aware actions
      setMode: (mode) => set({ mode }),

      setCalendarMappingId: (id) => set({ calendarMappingId: id }),

      setVariationId: (id) => set({ variationId: id }),

      setSaveTarget: (target) => set({ saveTarget: target }),

      setIsReschedule: (isReschedule) => set({ isReschedule }),
      // Clearing the key also clears the frozen shoot_now timestamp (same intent lifetime).
      setCreateIntentKey: (key) => set(key === null ? { createIntentKey: null, createShootAt: null } : { createIntentKey: key }),
      setCreateShootAt: (iso) => set({ createShootAt: iso }),

      /**
       * Load content from a calendar mapping
       * Supports both post-only and post+variation mappings
       * Variation content takes priority when present
       */
      loadFromMapping: (mapping: CalendarMappingData) => {
        // Resolve content: variation → post → empty
        const hasVariation = !!mapping.variation;
        const sourceCaption = hasVariation
          ? (mapping.variation?.caption || mapping.post?.caption || '')
          : (mapping.post?.caption || '');
        const sourceMedia = hasVariation
          ? (mapping.variation?.postMedia || mapping.post?.postMedia || [])
          : (mapping.post?.postMedia || []);
        const sourceLabels = hasVariation
          ? (mapping.variation?.labels || mapping.post?.labels || [])
          : (mapping.post?.labels || []);

        // Convert PostMedia to MediaItem format (reuse existing postMediaToMediaItem)
        const mediaItems = sourceMedia.map((pm) => postMediaToMediaItem(pm as PostMedia));

        // Determine save target
        const saveTarget = hasVariation ? 'variation' as const : 'post' as const;

        set({
          mode: 'commit_mapped',
          draftOwner: null,
          calendarMappingId: mapping.id,
          variationId: mapping.variation?.id || null,
          saveTarget,
          caption: sourceCaption,
          selectedChannels: [],
          media: mediaItems,
          labels: sourceLabels,
          calendarId: mapping.calendarId,
          scheduledTime: mapping.plannedDate ? new Date(mapping.plannedDate) : null,
          editingPostId: mapping.post?.id || null,
          originalPost: null, // Will be set if needed
          existingMediaIds: sourceMedia.map(m => m.id),
          removedMediaIds: [],
          isCarouselMode: sourceMedia.length > 1,
        });

        devLog.log('[ComposerStore] Loaded from mapping', {
          mappingId: mapping.id,
          hasVariation,
          saveTarget,
          mediaCount: mediaItems.length,
        });
      },
    }),
    {
      name: 'ComposerStore',
      enabled: import.meta.env.DEV, // Only enable in development
    }
  )
);
